import React from 'react';
import { Tag, Button, Tooltip, Avatar, Modal, message, Popover, QRCode, Badge } from 'antd';
import { ExclamationCircleOutlined, ExperimentOutlined, RobotOutlined, PlusOutlined, GlobalOutlined, WechatOutlined, GithubOutlined, GitlabOutlined, CodeOutlined, LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router';
import WscDictMapping from "@/component/dictmapping";
import useRequest from '@/utils/useRequest';

const AppCard = ({ app, showDynamicModal, onRefresh }) => {
  const navigate = useNavigate();
  const { request } = useRequest();
  
  // 获取CICD状态图标
  const getCICDStatusIcon = (status) => {
    switch (status) {
      case 'in_progress':
        return <LoadingOutlined style={{ color: '#1890ff', fontSize: '16px' }} spin />;
      case 'success':
        return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />;
      case 'failed':
        return <CloseCircleOutlined style={{ color: '#f5222d', fontSize: '16px' }} />;
      default:
        return null;
    }
  };
  
  // 获取CICD状态文本
  const getCICDStatusText = (status) => {
    switch (status) {
      case 'in_progress':
        return 'CICD进行中';
      case 'success':
        return 'CICD成功';
      case 'failed':
        return 'CICD失败';
      default:
        return '未知状态';
    }
  };

  // 获取仓库图标
  const getRepositoryIcon = (repoUrl) => {
    if (!repoUrl) return <CodeOutlined />;
    
    if (repoUrl.includes('github.com')) {
      return <GithubOutlined style={{ fontSize: '18px' }} />;
    } else if (repoUrl.includes('gitlab')) {
      return <GitlabOutlined style={{ fontSize: '18px', color: '#FC6D26' }} />;
    } else {
      return <CodeOutlined style={{ fontSize: '18px' }} />;
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: '确认删除',
      // ... 删除逻辑保持不变
      onOk: async () => { 
          const result = await request("/api/v2/private/project/application/delete", "POST", "json", { Id: app.Id });
          if (result.success) {
            message.success('删除成功');
            onRefresh();
          } else {
            message.error('删除失败');
          }
       }
  });
  };

  return (
    <div className="border rounded-md p-3 bg-white shadow-sm hover:shadow-md transition-shadow">
      <div className="flex justify-between items-center mb-2">
        <Tag color="blue" className="text-base px-2 py-1">{app.Name}</Tag>
        <div className="flex space-x-1">
          {/* 仅保留仓库图标 */}
          <div className="flex items-center mr-2">
            {app.Repository && (
              <Tooltip title={`仓库: ${app.Repository}`}>
                <a 
                  href={app.Repository} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-gray-600 hover:text-blue-500"
                >
                  {getRepositoryIcon(app.Repository)}
                </a>
              </Tooltip>
            )}
          </div>
          
          <Button 
            type="default" 
            size="small"
            onClick={() => showDynamicModal(209, app)}
          >
            修改
          </Button>
          <Button 
            type="default" 
            danger
            size="small"
            onClick={handleDelete}
          >
            删除
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="flex items-center">
          <span className="text-gray-500 mr-1">类型:</span>
          <WscDictMapping dictName={"AppType"} dictValue={app.Type}></WscDictMapping>
        </div>
        <div className="flex items-center">
          <span className="text-gray-500 mr-1">版本:</span>
          <div className="flex flex-col flex-grow">
            {app.Version && (
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center">
                  <Tag color="green" className="mr-1">
                    正式: {app.Version}
                  </Tag>
                  {app.ProductionUrl && (
                    app.Type === 'miniprogram' ? (
                      <Popover 
                        content={
                          <div className="text-center">
                            <QRCode value={app.ProductionUrl} size={120} />
                            <div className="mt-2 text-xs text-gray-500">扫码访问正式版小程序</div>
                          </div>
                        } 
                        title="正式版小程序二维码"
                        trigger="hover"
                      >
                        <WechatOutlined className="text-green-500 cursor-pointer" style={{ fontSize: '16px' }} />
                      </Popover>
                    ) : (
                      <Tooltip title={app.ProductionUrl}>
                        <a 
                          href={app.ProductionUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <GlobalOutlined className="text-blue-500" style={{ fontSize: '16px' }} />
                        </a>
                      </Tooltip>
                    )
                  )}
                </div>
                <Tooltip title="发布新版本">
                  <Button 
                    type="primary" 
                    size="small" 
                    shape="circle"
                    icon={<RobotOutlined />}
                    onClick={async () => {
                      const result = await request(
                        "/api/v2/private/application/version",
                        "POST",
                        "json",
                        { appId: app.Id, branch: 'master' }
                      );
                      if (result.success) {
                        // 合并版本信息到app对象
                        const appWithVersion = {
                          ...app,
                          AppId: app.Id,
                          host_url: app.ProductionUrl,
                          scriptfile: app.ScriptFilename,
                          server: app.ProdServer,
                          port: app.ProdPort,
                          path: app.ProdPath,
                          branch: 'master',
                          PrevVersion: result.data.version,
                          BuildVersion: result.data.new_version
                        };
                        showDynamicModal(210, appWithVersion);
                      } else {
                        message.error('获取版本信息失败');
                      }
                      //showDynamicModal(210, app)
                    }}
                  />
                </Tooltip>
              </div>
            )}
            
            {app.TestVersion && (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Tag color="orange" className="mr-1">
                    测试: {app.TestVersion}
                  </Tag>
                  {/* 添加状态图标 */}
                  {app.TestCICDStatus && (
                    <Tooltip title={getCICDStatusText(app.TestCICDStatus)}>
                      <span className="mr-2">{getCICDStatusIcon(app.TestCICDStatus)}</span>
                    </Tooltip>
                  )}
                  {app.TestUrl && (
                    app.Type === 'miniprogram' ? (
                      <Popover 
                        content={
                          <div className="text-center">
                            <QRCode value={app.TestUrl} size={120} />
                            <div className="mt-2 text-xs text-gray-500">扫码访问测试版小程序</div>
                          </div>
                        } 
                        title="测试版小程序二维码"
                        trigger="hover"
                      >
                        <WechatOutlined className="text-orange-500 cursor-pointer" style={{ fontSize: '16px' }} />
                      </Popover>
                    ) : (
                      <Tooltip title={app.TestUrl}>
                        <a 
                          href={app.TestUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <GlobalOutlined className="text-orange-500" style={{ fontSize: '16px' }} />
                        </a>
                      </Tooltip>
                    )
                  )}
                </div>
                <Tooltip title={app.TestCICDStatus === "in_progress" ? "CICD进行中，请稍后再试" : "发布测试版"}>
                  <Button 
                    type="default" 
                    size="small" 
                    shape="circle"
                    icon={app.TestCICDStatus === "in_progress" ? <LoadingOutlined /> : <ExperimentOutlined />}
                    onClick={async () => {
                      const result = await request(
                        "/api/v2/private/application/version",
                        "POST",
                        "json",
                        { appId: app.Id, branch: 'dev' }
                      );
                      if (result.success) {
                        // 合并版本信息到app对象
                        const appWithVersion = {
                          ...app,
                          AppId: app.Id,
                          host_url: app.TestUrl,
                          scriptfile: app.ScriptFilename,
                          server: app.TestServer,
                          port: app.TestPort,
                          path: app.TestPath,
                          branch: 'dev',
                          PrevVersion: result.data.version,
                          BuildVersion: result.data.new_version
                        };
                        showDynamicModal(210, appWithVersion);
                      } else {
                        message.error('获取版本信息失败');
                      }
                    }}
                    disabled={app.TestCICDStatus === "in_progress"}
                    className={app.TestCICDStatus === "in_progress" ? "animate-pulse" : ""}
                  />
                </Tooltip>
              </div>
            )}
            
            
            {!app.Version && !app.TestVersion && (
              <div className="flex items-center justify-between">
                <span className="text-gray-400">未设置</span>
                <Tooltip title="创建首个版本">
                  <Button 
                    type="primary" 
                    size="small" 
                    shape="circle"
                    icon={<PlusOutlined />}
                    onClick={() => navigate(`/cicd/${app.Id}?type=new`)}
                  />
                </Tooltip>
              </div>
            )}
          </div>
        </div>
        
        <div className="col-span-2 mt-2">
          <div className="flex items-center">
            <span className="text-gray-500 mr-2">负责人:</span>
            {/* 负责人部分保持不变 */}
          </div>
        </div>
        {/* 参与人部分保持不变 */}
      </div>
    </div>
  );
};

export default AppCard;